import aolNepalImage1 from "../assets/AOL_Nepal_1.jpeg";
import aolNepalImage2 from   "../assets/AOL_Nepal_2.jpeg";
import pmReliefImage1 from "../assets/PM_Fund_Nepal_1.jpeg";
import pmReliefImage2 from "../assets/PM_Fund_Nepal_2.jpeg";
type UnknownRecord = Record<string, unknown>;

export type ImpactBankDetails = {
  bankName?: string;
  accountHolder?: string;
  accountNumber?: string;
  swiftCode?: string;
  reference?: string;
  instructions?: string;
};

export const DEFAULT_IMPACT_BANK_DETAILS: ImpactBankDetails = {
  bankName: "Shinhan Bank",
  accountHolder: "Card Creator",
  accountNumber: "110-123-456789",
  swiftCode: "SHBKKRSE",
  reference: "HOPE-<YourName>",
  instructions:
    "Transfer to the card creator's account. After they verify receipt, funds are directed to PM Relief Fund Nepal and Art of Living Nepal.",
};

export function creatorBankDetails(creatorName?: string): ImpactBankDetails {
  const name = creatorName?.trim();
  return {
    ...DEFAULT_IMPACT_BANK_DETAILS,
    accountHolder: name || DEFAULT_IMPACT_BANK_DETAILS.accountHolder,
  };
}

export function resolveImpactBankDetails(
  details?: ImpactBankDetails | null,
  creatorName?: string
): ImpactBankDetails {
  const fallback = creatorBankDetails(creatorName);
  if (!details?.bankName && !details?.accountNumber && !details?.accountHolder) {
    return fallback;
  }
  return {
    ...fallback,
    ...details,
    accountHolder: details.accountHolder || fallback.accountHolder,
  };
}

export type ImpactRecipientImage = {
  url: string;
  title?: string;
  caption?: string;
  credit?: string;
  sourceUrl?: string;
};

export type ImpactRecipientVideo = {
  url: string;
  title?: string;
  description?: string;
};

export type ImpactFundRecipient = {
  key: string;
  name: string;
  role?: string;
  tagline?: string;
  blurb?: string;
  description?: string;
  websiteUrl?: string;
  videoUrl?: string;
  instagramUrl?: string;
  instagramHandle?: string;
  heroImageUrl?: string;
  imageUrls?: string[];
  focusAreas?: string[];
  sectionKey?: "recipientPm" | "recipientAol";
  about?: string;
  whatTheyDo?: string;
  /** Forward-looking copy: what this campaign's funds are meant to enable. */
  whatFundsWillDo?: string;
  workImages?: ImpactRecipientImage[];
  workVideos?: ImpactRecipientVideo[];
};

export type ImpactUseOfFund = {
  title: string;
  description?: string;
  estimatedAmount?: number;
  raisedAmount?: number;
  imageUrl?: string;
};

export type ImpactBeneficiary = {
  name?: string;
  type?: string;
  description?: string;
};

export type ImpactLocalPartner = {
  userId: number;
  username?: string;
  name?: string;
  profileImage?: string;
  assigned: boolean;
  assignedAt?: string;
};

export function readImpactLocalPartner(source: unknown): ImpactLocalPartner | undefined {
  const root = asRecord(source);
  const nestedContent = asRecord(root.content);
  const partnerValue = asRecord(
    root.partner ??
      nestedContent.partner ??
      root.local_partner ??
      nestedContent.local_partner ??
      root.localPartner ??
      nestedContent.localPartner ??
      root.invited_partner ??
      nestedContent.invited_partner
  );
  const partnerUser = asRecord(partnerValue.user);
  const partnerUserId =
    asNumber(partnerValue.user_id) ??
    asNumber(partnerValue.userId) ??
    asNumber(partnerUser.id) ??
    asNumber(partnerUser.user_id) ??
    asNumber(partnerValue.id) ??
    asNumber(root.partner_id) ??
    asNumber(nestedContent.partner_id);
  if (partnerUserId == null || partnerUserId <= 0) return undefined;

  const firstName = asString(partnerValue.first_name) ?? asString(partnerUser.first_name);
  const lastName = asString(partnerValue.last_name) ?? asString(partnerUser.last_name);
  const username = asString(partnerValue.username) ?? asString(partnerUser.username);

  return {
    userId: partnerUserId,
    username,
    name:
      asString(partnerValue.name) ??
      ([firstName, lastName].filter(Boolean).join(" ") || username),
    profileImage:
      asString(partnerValue.profile_image) ??
      asString(partnerValue.profileImage) ??
      asString(partnerUser.profile_image),
    assigned: partnerValue.assigned !== false,
    assignedAt: asString(partnerValue.assigned_at) ?? asString(partnerValue.assignedAt),
  };
}

export function isInvitedImpactPartnerViewer(
  partner: ImpactLocalPartner | undefined | null,
  viewerIds?: Array<number | string | null | undefined>
): boolean {
  if (!partner?.assigned) return false;
  const partnerId = Number(partner.userId);
  if (!Number.isFinite(partnerId) || partnerId <= 0) return false;
  return (viewerIds ?? []).some((id) => {
    const viewer = Number(id);
    return Number.isFinite(viewer) && viewer > 0 && viewer === partnerId;
  });
}

export type ImpactFieldUpdate = {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  imageUrls?: string[];
  imageContext?: string;
  videoUrl?: string;
  videoContext?: string;
  locationName?: string;
  mapsUrl?: string;
  latitude?: number;
  longitude?: number;
  allocatedTask?: string;
  allocatedAmount?: number;
  status: "pending" | "approved" | "rejected";
  createdByUserId?: number;
  createdAt?: string | number | null;
  attachToCubeId?: string;
};

export const DEFAULT_FUND_RECIPIENTS: ImpactFundRecipient[] = [
  {
    key: "pm-relief",
    name: "Nepal Prime Minister's Relief Fund",
    role: "정부 주도의 재난 구호 지원",
    tagline: "정부 주도의 재난 구호 지원",
    blurb: "네팔 정부가 주도하는 구호와 복구 활동을 지원합니다.",
    description:
      "네팔 총리 재난구호기금(PMDRF)은 네팔 전역에서 자연재해로 피해를 입은 주민들에게 긴급 지원을 제공합니다. 네팔 정부와 협력하여 긴급 대응, 구호 물품 지원 및 복구 사업을 추진합니다.",
    about:
      "네팔 총리 재난구호기금은 재난 대응과 복구를 위한 후원금을 접수하고 필요한 곳에 전달하는 네팔 정부의 공식 기금입니다.",
    whatTheyDo:
      "정부의 구호 우선순위에 따라 재난 피해 주민과 지역사회에 긴급 물품, 임시 거처, 의료 지원 및 복구 자원을 제공합니다.",
    whatFundsWillDo:
      "이곳에 전달된 후원금은 홍수 대응을 위한 국가 구호기금에 더해져 긴급 물품, 임시 거처, 식량과 의료 지원, 그리고 장기적인 재건에 사용됩니다.",
    workImages: [
      {
        url: pmReliefImage1,
        title: "구호 물품 전달",
        caption: "피해 지역 주민들에게 구호 물품을 전달합니다.",
        credit: "Nepal Prime Minister's Relief Fund",
      },
      {
        url: pmReliefImage2,
        title: "복구와 재건",
        caption: "홍수 피해 지역의 회복과 재건을 지원합니다.",
        credit: "Nepal Prime Minister's Relief Fund",
      },
    ],
    workVideos: [
      {
        url: "https://www.youtube.com/watch?v=MhYVDPkFOD4",
        title: "네팔 구호 활동",
        description: "네팔에서 진행되는 긴급 구호와 복구 활동을 소개합니다.",
      },
      {
        url: "https://www.youtube.com/watch?v=hUQbst8FiHg",
        title: "홍수 대응 협력",
        description: "홍수 당시 구조 활동과 정부 주도의 공동 대응 과정을 보여 줍니다.",
      },
    ],
    websiteUrl: "https://pmdrf.nchl.com.np/",
    videoUrl: "https://www.youtube.com/watch?v=MhYVDPkFOD4",
    heroImageUrl:
    pmReliefImage1,
    focusAreas: [
      "긴급 구호",
      "피해 가정 지원",
      "복구 및 재건",
      "재난 대비",
    ],
    sectionKey: "recipientPm",
  },
  {
    key: "aol",
    name: "Art of Living Nepal",
    role: "사람을 치유하고 회복력 있는 공동체를 만듭니다.",
    tagline: "사람을 치유하고 회복력 있는 공동체를 만듭니다.",
    blurb: "지역사회의 웰빙과 장기적인 회복을 지원합니다.",
    description:
      "Art of Living Nepal은 요가, 명상, 호흡 수련과 지역사회 봉사를 통해 주민들의 웰빙을 증진합니다. 재난 상황에서는 자원봉사자를 조직해 구호 활동을 지원하고 공동체가 치유와 재건을 이어가도록 돕습니다.",
    about:
      "Art of Living Nepal은 국제적인 Art of Living 활동의 네팔 지부로, 웰빙과 요가, 명상, 호흡 수련을 중심으로 다양한 지역사회 프로그램을 운영합니다.",
    whatTheyDo:
      "주민들의 심리적 안정과 회복을 돕는 웰빙 프로그램을 운영하고, 자원봉사자들과 함께 구호 물품 전달 및 지역사회 지원 활동을 진행합니다.",
    whatFundsWillDo:
      "이곳에 전달된 후원금은 홍수 피해 지역에 구호 물품을 전달하고, 피해 가족들이 재난 이후 안정을 되찾도록 돕는 트라우마 완화 및 웰빙 프로그램에 사용됩니다.",
    workImages: [
      {
        url: aolNepalImage1,
        title: "Art of Living Nepal",
        caption: "자원봉사자들이 지역 주민들을 지원합니다.",
        credit: "Art of Living Nepal",
      },
      {
        url: aolNepalImage2,
        title: "Art of Living Nepal",
        caption: "치유와 회복을 통해 더 단단한 공동체를 만듭니다.",
        credit: "Art of Living Nepal",
      },
    ],
    workVideos: [
      {
        url: "https://www.youtube.com/shorts/9IP6sbSJwHY",
        title: "네팔 전역에 전달된 구호",
        description: "자원봉사자들이 홍수로 고립된 가족들에게 구호 물품을 전달합니다.",
      },
      {
        url: "https://www.youtube.com/shorts/9HuFV5CfSo4",
        title: "네팔을 향한 연대의 메시지",
        description: "홍수 피해를 입은 네팔 국민에게 전하는 위로와 연대의 메시지입니다.",
      },
    ],
    websiteUrl: "https://www.instagram.com/artoflivingnepal/",
    instagramUrl: "https://www.instagram.com/artoflivingnepal/",
    instagramHandle: "artoflivingnepal",
    heroImageUrl:
      "",
    imageUrls: [
      "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80",
    ],
    focusAreas: [
      "지역사회 웰빙",
      "자원봉사 활동",
      "인도적 지원",
      "장기적 회복력",
    ],
    sectionKey: "recipientAol",
  },
];

export function mergeFundRecipients(list: ImpactFundRecipient[]): ImpactFundRecipient[] {
  const merged = DEFAULT_FUND_RECIPIENTS.map((def) => {
    const found = list.find(
      (item) => item.sectionKey === def.sectionKey || item.key === def.key
    );
    if (!found) return def;
    return {
      ...def,
      ...found,
      tagline: found.tagline || def.tagline,
      blurb: found.blurb || def.blurb,
      description: found.description || def.description,
      about: found.about || def.about,
      whatTheyDo: found.whatTheyDo || def.whatTheyDo,
      whatFundsWillDo: found.whatFundsWillDo || def.whatFundsWillDo,
      workImages: found.workImages?.length ? found.workImages : def.workImages,
      workVideos: found.workVideos?.length ? found.workVideos : def.workVideos,
      websiteUrl: found.websiteUrl || def.websiteUrl,
      videoUrl: found.videoUrl || def.videoUrl,
      instagramUrl: found.instagramUrl || def.instagramUrl,
      instagramHandle: found.instagramHandle || def.instagramHandle,
      heroImageUrl: found.heroImageUrl || def.heroImageUrl,
      imageUrls: found.imageUrls?.length ? found.imageUrls : def.imageUrls,
      focusAreas: found.focusAreas?.length ? found.focusAreas : def.focusAreas,
      role: found.role && found.role !== "Intended recipient" ? found.role : def.role,
    };
  });
  const extras = list.filter(
    (item) =>
      !merged.some((row) => row.key === item.key || row.sectionKey === item.sectionKey)
  );
  return [...merged, ...extras];
}

export type ImpactCardData = {
  causeCategory?: string;
  causeSummary?: string;
  location?: string;
  impactGoal?: string;
  expectedOutcome?: string;
  targetAmount?: number;
  currency?: string;
  committedFund?: number;
  verifiedFund?: number;
  progressPercentage?: number;
  contributorCount?: number;
  collectorCount?: number;
  clickedCount?: number;
  viewedCount?: number;
  useOfFunds: ImpactUseOfFund[];
  beneficiary?: ImpactBeneficiary;
  projectStatus?: "Planning" | "Fundraising" | "In Progress" | "Completed";
  contributors: Array<{
    id: number | string;
    name: string;
    amount: number;
    verifiedAmount?: number;
    status: "Committed" | "Pending" | "Verified" | "Rejected";
    contributedAt: string;
    userId?: number;
    message?: string;
    accountType?: string;
    purchaseId?: number | string;
  }>;
  bankDetails?: ImpactBankDetails;
  fundRecipients: ImpactFundRecipient[];
  isDemoData?: boolean;
  localPartner?: ImpactLocalPartner;
  impactUpdates: ImpactFieldUpdate[];
};

const asRecord = (value: unknown): UnknownRecord =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};

const asString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text || undefined;
};

const asNumber = (value: unknown): number | undefined => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : undefined;
};

const formatLocation = (value: unknown): string | undefined => {
  if (typeof value === "string") return asString(value);

  const location = asRecord(value);
  const parts = [
    location.location_name,
    location.municipality,
    location.district,
    location.province,
    location.country,
  ]
    .map(asString)
    .filter((part): part is string => Boolean(part));

  return [...new Set(parts)].join(", ") || undefined;
};

function pickBankDetails(value: unknown): ImpactBankDetails {
  const item = asRecord(value);
  return {
    bankName: asString(item.bank_name) ?? asString(item.bankName),
    accountHolder:
      asString(item.account_holder) ??
      asString(item.accountHolder) ??
      asString(item.account_name) ??
      asString(item.accountName),
    accountNumber:
      asString(item.account_number) ??
      asString(item.accountNumber) ??
      asString(item.bank_account) ??
      asString(item.bankAccount),
    swiftCode: asString(item.swift_code) ?? asString(item.swiftCode) ?? asString(item.swift),
    reference: asString(item.reference),
    instructions: asString(item.instructions),
  };
}

function hasBankDetails(details: ImpactBankDetails) {
  return Boolean(details.bankName || details.accountHolder || details.accountNumber);
}

export function getImpactCardData(contentValue: unknown): ImpactCardData {
  const content = asRecord(contentValue);
  const beneficiaryValue = asRecord(content.beneficiary);

  const useOfFunds = Array.isArray(content.use_of_funds)
    ? content.use_of_funds.flatMap((value): ImpactUseOfFund[] => {
        const item = asRecord(value);
        const title = asString(item.title);
        if (!title) return [];
        return [
          {
            title,
            description: asString(item.description),
            estimatedAmount: asNumber(item.estimated_amount) ?? asNumber(item.estimatedAmount),
            raisedAmount: asNumber(item.raised_amount) ?? asNumber(item.raisedAmount),
            imageUrl: asString(item.image_url) ?? asString(item.imageUrl),
          },
        ];
      })
    : [];

  const impactUpdates = Array.isArray(content.impact_updates)
    ? content.impact_updates.flatMap((value, index): ImpactFieldUpdate[] => {
        const item = asRecord(value);
        const title = asString(item.title);
        if (!title) return [];
        const rawStatus = asString(item.status)?.toLowerCase();
        return [
          {
            id: String(
              (typeof item.id === "string" || typeof item.id === "number") &&
                String(item.id).trim()
                ? item.id
                : index
            ),
            title,
            description: asString(item.description),
            imageUrl: asString(item.image_url) ?? asString(item.imageUrl),
            imageContext: asString(item.image_context) ?? asString(item.imageContext),
            videoUrl: asString(item.video_url) ?? asString(item.videoUrl),
            videoContext: asString(item.video_context) ?? asString(item.videoContext),
            locationName: asString(item.location_name) ?? asString(item.locationName),
            mapsUrl: asString(item.maps_url) ?? asString(item.mapsUrl),
            latitude: asNumber(item.latitude),
            longitude: asNumber(item.longitude),
            allocatedTask: asString(item.allocated_task) ?? asString(item.allocatedTask),
            allocatedAmount: asNumber(item.allocated_amount) ?? asNumber(item.allocatedAmount),
            status:
              rawStatus === "approved" || rawStatus === "verified"
                ? "approved"
                : rawStatus === "rejected"
                  ? "rejected"
                  : "pending",
            createdByUserId: asNumber(item.created_by_user_id) ?? asNumber(item.createdByUserId),
            createdAt:
              typeof item.created_at === "string" || typeof item.created_at === "number"
                ? item.created_at
                : typeof item.createdAt === "string" || typeof item.createdAt === "number"
                  ? item.createdAt
                  : null,
          },
        ];
      })
    : [];

  const localPartner = readImpactLocalPartner(content);
  const talent = asRecord(content.talent);
  const bankDetails =
    [
      pickBankDetails(content.bank_details ?? content.bankDetails),
      pickBankDetails(talent.bank_details ?? talent.bankDetails ?? talent.bank),
      pickBankDetails({
        bank_name: talent.bank_name,
        account_holder: talent.account_holder ?? talent.account_name,
        account_number: talent.account_number ?? talent.bank_account,
        swift_code: talent.swift_code,
      }),
    ].find(hasBankDetails) ?? pickBankDetails(content.bank_details ?? content.bankDetails);

  const fundRecipients = Array.isArray(content.fund_recipients)
    ? content.fund_recipients.flatMap((value): ImpactFundRecipient[] => {
        const item = asRecord(value);
        const name = asString(item.name) ?? asString(item.title);
        if (!name) return [];
        const images = Array.isArray(item.image_urls)
          ? item.image_urls.filter((url): url is string => typeof url === "string" && Boolean(url.trim()))
          : [];
        const key = asString(item.key) ?? name.toLowerCase().replace(/\s+/g, "-");
        const sectionRaw = asString(item.section_key) ?? asString(item.sectionKey);
        return [
          {
            key,
            name,
            role: asString(item.role),
            tagline: asString(item.tagline),
            blurb: asString(item.blurb),
            description: asString(item.description),
            about: asString(item.about),
            whatTheyDo: asString(item.what_they_do) ?? asString(item.whatTheyDo),
            whatFundsWillDo:
              asString(item.what_funds_will_do) ?? asString(item.whatFundsWillDo),
            workImages: (Array.isArray(item.work_images) ? item.work_images : Array.isArray(item.workImages) ? item.workImages : []).flatMap((value): ImpactRecipientImage[] => {
              const image = asRecord(value);
              const url = asString(image.url);
              if (!url) return [];
              return [{
                url,
                title: asString(image.title),
                caption: asString(image.caption),
                credit: asString(image.credit),
                sourceUrl: asString(image.source_url) ?? asString(image.sourceUrl),
              }];
            }).slice(0, 2),
            workVideos: (Array.isArray(item.work_videos) ? item.work_videos : Array.isArray(item.workVideos) ? item.workVideos : []).flatMap((value): ImpactRecipientVideo[] => {
              const video = asRecord(value);
              const url = asString(video.url) ?? asString(video.youtube_url) ?? asString(video.youtubeUrl);
              if (!url) return [];
              return [{
                url,
                title: asString(video.title),
                description: asString(video.description),
              }];
            }).slice(0, 2),
            websiteUrl: asString(item.website_url) ?? asString(item.websiteUrl),
            videoUrl: asString(item.video_url) ?? asString(item.videoUrl),
            instagramUrl: asString(item.instagram_url) ?? asString(item.instagramUrl),
            instagramHandle: asString(item.instagram_handle) ?? asString(item.instagramHandle),
            heroImageUrl: asString(item.hero_image_url) ?? asString(item.heroImageUrl),
            imageUrls: images.length ? images : undefined,
            focusAreas: Array.isArray(item.focus_areas)
              ? item.focus_areas.filter((value): value is string => typeof value === "string" && Boolean(value.trim()))
              : Array.isArray(item.focusAreas)
                ? item.focusAreas.filter((value): value is string => typeof value === "string" && Boolean(value.trim()))
                : undefined,
            sectionKey:
              sectionRaw === "recipientAol" || key.includes("aol")
                ? "recipientAol"
                : "recipientPm",
          },
        ];
      })
    : [];

  const beneficiary: ImpactBeneficiary = {
    name: asString(beneficiaryValue.beneficiary_name),
    type: asString(beneficiaryValue.beneficiary_type),
    description: asString(beneficiaryValue.beneficiary_description),
  };

  return {
    causeCategory: asString(content.cause_category),
    causeSummary: asString(content.cause_summary),
    location: formatLocation(content.location),
    impactGoal: asString(content.impact_goal),
    expectedOutcome: asString(content.expected_outcome),
    targetAmount: asNumber(content.target_amount),
    currency: asString(content.currency),
    committedFund: asNumber(content.committed_fund),
    verifiedFund: asNumber(content.verified_fund),
    progressPercentage: asNumber(content.progress_percentage),
    contributorCount: asNumber(content.contributor_count) ?? asNumber(content.contributorCount),
    collectorCount: asNumber(content.collector_count) ?? asNumber(content.collectorCount),
    clickedCount: asNumber(content.clicked_count) ?? asNumber(content.clickedCount),
    viewedCount: asNumber(content.viewed_count) ?? asNumber(content.viewedCount),
    useOfFunds,
    localPartner,
    bankDetails:
      bankDetails.bankName || bankDetails.accountHolder || bankDetails.accountNumber || bankDetails.instructions
        ? bankDetails
        : undefined,
    fundRecipients: mergeFundRecipients(fundRecipients),
    beneficiary:
      beneficiary.name || beneficiary.type || beneficiary.description ? beneficiary : undefined,
    projectStatus: asString(content.project_status) as ImpactCardData["projectStatus"],
    contributors: Array.isArray(content.contributors)
      ? content.contributors.flatMap((value, index) => {
          const contributor = asRecord(value);
          const name = asString(contributor.name);
          const amount = asNumber(contributor.amount);
          if (!name || amount == null) return [];
          return [
            {
              id:
                typeof contributor.id === "string" || typeof contributor.id === "number"
                  ? contributor.id
                  : index,
              name,
              amount,
              status:
                contributor.status === "Pending" || contributor.status === "Committed"
                  ? "Committed"
                  : contributor.status === "Rejected"
                    ? "Rejected"
                    : "Verified",
              contributedAt:
                asString(contributor.contributed_at) ?? new Date().toISOString(),
              message: asString(contributor.message),
            },
          ];
        })
      : [],
    impactUpdates,
  };
}

/** Temporary presentation data until contribution/reporting endpoints are available. */
export function withImpactCardDemoData(data: ImpactCardData): ImpactCardData {
  const needsDemoFunding =
    data.verifiedFund == null &&
    data.committedFund == null &&
    data.contributorCount == null &&
    data.collectorCount == null;

  if (!needsDemoFunding) return data;

  return {
    ...data,
    verifiedFund: 186_500,
    committedFund: 214_000,
    progressPercentage:
      data.targetAmount && data.targetAmount > 0
        ? (186_500 / data.targetAmount) * 100
        : 37.3,
    contributorCount: 47,
    collectorCount: 126,
    projectStatus: data.projectStatus ?? "Fundraising",
    contributors: [
      {
        id: "demo-1",
        name: "Aarav S.",
        amount: 25_000,
        status: "Verified",
        contributedAt: "2026-09-12T09:20:00.000Z",
      },
      {
        id: "demo-2",
        name: "Mina K.",
        amount: 15_000,
        status: "Verified",
        contributedAt: "2026-09-11T14:45:00.000Z",
      },
      {
        id: "demo-3",
        name: "Community Reader",
        amount: 10_000,
        status: "Committed",
        contributedAt: "2026-09-10T07:30:00.000Z",
      },
    ],
    impactUpdates:
      data.impactUpdates.length > 0
        ? data.impactUpdates
        : [
            {
              id: "demo-update-1",
              title: "School and library room confirmed",
              description:
                "The school has approved the room that will become the new community library.",
              createdAt: "2026-09-08T10:00:00.000Z",
              status: "approved",
            },
            {
              id: "demo-update-2",
              title: "First book supplier quotations received",
              description:
                "Local suppliers submitted quotations for Nepali and English learning materials.",
              createdAt: "2026-09-13T10:00:00.000Z",
              status: "approved",
            },
          ],
    isDemoData: true,
  };
}

export function serializeImpactFieldUpdate(update: ImpactFieldUpdate): Record<string, unknown> {
  return {
    id: update.id,
    title: update.title,
    description: update.description ?? "",
    image_url: update.imageUrl ?? "",
    image_context: update.imageContext ?? "",
    video_url: update.videoUrl ?? "",
    video_context: update.videoContext ?? "",
    location_name: update.locationName ?? "",
    maps_url: update.mapsUrl ?? "",
    latitude: update.latitude ?? null,
    longitude: update.longitude ?? null,
    allocated_task: update.allocatedTask ?? "",
    allocated_amount: update.allocatedAmount ?? null,
    status: update.status,
    created_by_user_id: update.createdByUserId ?? null,
    created_at: update.createdAt ?? null,
  };
}

export function serializeImpactLocalPartner(
  partner: ImpactLocalPartner
): Record<string, unknown> {
  return {
    user_id: partner.userId,
    username: partner.username ?? "",
    name: partner.name ?? "",
    profile_image: partner.profileImage ?? "",
    assigned: partner.assigned,
    assigned_at: partner.assignedAt ?? new Date().toISOString(),
  };
}
